import { runCapabilityProbe } from './probe/capabilities';
import { DriveDeckTransportClient } from './transport/client';
import { createRenderer } from './renderer/types';
import { HudOverlay } from './hud/overlay';
import { InputCaptureEngine } from './input/capture';

async function bootstrap() {
  console.log('[DriveDeck] Initializing Tesla Web Viewer...');

  const canvas = document.getElementById('projection-canvas') as HTMLCanvasElement;
  const hud = new HudOverlay();

  // 1. Run Capability Probe
  const probeReport = await runCapabilityProbe();
  hud.displayCapabilityReport(probeReport);
  console.log('[DriveDeck] Capability Probe completed:', probeReport);

  // 2. Initialize Renderer based on probe report
  let client: DriveDeckTransportClient | undefined;
  const renderer = await createRenderer(canvas, probeReport, () => client?.requestKeyframe());
  hud.setDecoderName(renderer.getName());

  // 3. Initialize Transport
  // Connect to relative endpoint /ws/tesla (same origin)
  const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/tesla`;

  let decodeErrorReported = false;
  let wasStreaming = false;
  client = new DriveDeckTransportClient(wsUrl, {
    onServerStateChange: (state) => hud.setServerState(state),
    onPhoneStateChange: (connected) => hud.setPhoneState(connected),
    onStreamStateChange: (active) => {
      hud.setStreamState(active);
      if (!active) {
        hud.setFps(0);
        if (wasStreaming) renderer.reset();
      }
      wasStreaming = active;
    },
    onSessionStateChange: (paired) => hud.setSessionState(paired),
    onRttUpdate: (rttMs) => hud.setRtt(rttMs),
    onFrame: (frame) => {
      try {
        renderer.renderFrame(frame);
        hud.setFps(renderer.getFps());
        hud.setDecoderName(renderer.getName());
        decodeErrorReported = false;
      } catch (error) {
        hud.setStreamState(false);
        hud.setDecoderName(`Decode error: ${String(error)}`);
        if (!decodeErrorReported) {
          console.error("[DriveDeck] H.264 decode failed:", error);
          client?.requestKeyframe();
          decodeErrorReported = true;
        }
      }
    },
    onTransportModeChange: (mode) => hud.setTransportMode(mode),
    onLocalRedirectAvailable: (url) => hud.showLocalSwitchPrompt(url),
    onError: (err) => console.warn('[DriveDeck] Transport callback error:', err),
  });

  // 3. Setup Pairing Callback
  hud.onPairSubmit(async (code: string) => {
    try {
      const res = await fetch('/api/pair', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, clientCapabilities: probeReport }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.token) {
          client!.setToken(data.token);
          client!.connect();
          return true;
        }
      }
      return false;
    } catch (e) {
      console.error('[DriveDeck] Pairing API request failed:', e);
      return false;
    }
  });

  hud.onUnpair(() => {
    client!.unpair();
  });

  // 4. Input Capture
  new InputCaptureEngine(canvas, client);

  // 5. Connect only when paired; otherwise show pairing UI (no WebSocket retry loop).
  // Direct local viewer on port 8080 operates within the trusted hotspot subnet and bypasses cloud pairing tokens.
  const isDirectLocal = window.location.port === '8080' || client.getTransportMode() === 'local';
  if (client.getToken() || isDirectLocal) {
    client.connect();
    if (!isDirectLocal) {
      window.setTimeout(() => {
        client!.sendControl({
          type: 'capability_report',
          report: probeReport,
        });
      }, 1000);
    }
  } else {
    hud.setSessionState(false);
    hud.setServerState('disconnected');
  }
}

// Surface asset/decoder initialization failures in the existing diagnostics drawer.
function start(): void {
  void bootstrap().catch(error => {
    console.error('[DriveDeck] Viewer initialization failed:', error);
    const decoder = document.getElementById('hud-decoder');
    if (decoder) decoder.textContent = `Initialization error: ${String(error)}`;
    const stream = document.getElementById('hud-stream-status');
    if (stream) stream.textContent = 'STREAM: DECODER ERROR';
  });
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
